import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ChevronLeft, ChevronRight, Search, Loader2, Phone } from 'lucide-react';
import { BOARD_COLUMNS, ETAPA_COLORS, ETAPE, activeCells, statusLabel } from './crmLabels';

const PER_PAGE = [30, 50, 100];

const GROUPS = [1, 2, 3, 4].map((etapa) => ({
  etapa,
  label: ETAPE[etapa],
  span: BOARD_COLUMNS.filter((c) => c.etapa === etapa).length,
}));

export default function BoardView({ api, onOpenClient }) {
  const [data, setData] = useState({ rows: [], total: 0, page: 1, perPage: 30, pages: 1 });
  const [perPage, setPerPage] = useState(30);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
      if (search) params.set('q', search);
      setData(await api.get(`/board?${params.toString()}`));
    } finally {
      setLoading(false);
    }
  }, [api, page, perPage, search]);

  useEffect(() => { load(); }, [load]);

  const from = data.total ? (data.page - 1) * data.perPage + 1 : 0;
  const to = Math.min(data.page * data.perPage, data.total);

  return (
    <Card data-testid="crm-board">
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400" />
            <Input
              className="pl-8 w-56 h-9"
              placeholder="Caută nume sau telefon"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); setSearch(q.trim()); } }}
              data-testid="crm-board-search"
            />
          </div>
          <Button size="sm" variant="outline" onClick={() => { setPage(1); setSearch(q.trim()); }}
            data-testid="crm-board-search-btn">Caută</Button>
          {search && (
            <Button size="sm" variant="ghost" onClick={() => { setQ(''); setSearch(''); setPage(1); }}
              data-testid="crm-board-search-clear">Renunță la filtru</Button>
          )}
          <div className="ml-auto flex items-center gap-2 text-sm text-gray-600">
            <span data-testid="crm-board-count">
              {data.total ? `${from}–${to} din ${data.total}` : 'Niciun client'}
            </span>
            <select
              className="border rounded-md h-9 px-2 text-sm"
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              data-testid="crm-board-perpage"
            >
              {PER_PAGE.map((n) => <option key={n} value={n}>{n} / pagină</option>)}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto border rounded-lg relative">
          {loading && (
            <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20">
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
            </div>
          )}
          <table className="w-full border-collapse text-xs" data-testid="crm-board-table">
            <thead>
              <tr>
                <th rowSpan={2}
                  className="sticky left-0 z-10 bg-gray-100 border-b border-r px-3 py-2 text-left font-semibold text-gray-700 w-[170px] min-w-[170px]">
                  Client
                </th>
                {GROUPS.map((g) => (
                  <th key={g.etapa} colSpan={g.span}
                    className={`border-b border-l px-2 py-1.5 font-semibold text-center ${ETAPA_COLORS[g.etapa].head}`}>
                    {g.label}
                  </th>
                ))}
              </tr>
              <tr>
                {BOARD_COLUMNS.map((col) => (
                  <th key={col.key}
                    className={`border-b border-l px-1.5 py-2 font-medium text-center align-bottom w-[78px] ${ETAPA_COLORS[col.etapa].head}`}>
                    <span className="block leading-tight">{col.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && data.rows.length === 0 && (
                <tr>
                  <td colSpan={BOARD_COLUMNS.length + 1} className="px-3 py-8 text-center text-gray-500"
                    data-testid="crm-board-empty">
                    {search ? 'Niciun client nu se potrivește căutării.' : 'Niciun client activ în CRM.'}
                  </td>
                </tr>
              )}
              {data.rows.map((row) => {
                const cells = activeCells(row);
                return (
                  <tr key={row.solicitareId} className="hover:bg-gray-50"
                    data-testid={`crm-board-row-${row.clientId}`}>
                    <td className="sticky left-0 z-10 bg-white border-b border-r px-3 py-2">
                      <button onClick={() => onOpenClient(row.clientId)}
                        className="text-left hover:text-blue-700"
                        data-testid={`crm-board-name-${row.clientId}`}>
                        <span className="font-medium text-sm block truncate max-w-[152px]">{row.nume}</span>
                        <span className="text-[11px] text-gray-500 flex items-center gap-1">
                          <Phone className="h-3 w-3" />{row.telefon || '—'}
                        </span>
                      </button>
                    </td>
                    {BOARD_COLUMNS.map((col) => {
                      const mark = cells[col.key];
                      if (mark === undefined) {
                        return <td key={col.key} className="border-b border-l" />;
                      }
                      return (
                        <td key={col.key} className="border-b border-l p-1 text-center">
                          <button
                            onClick={() => onOpenClient(row.clientId)}
                            title={`${row.nume} — ${ETAPE[col.etapa]} · ${statusLabel(col.key)}${mark ? ` (${mark})` : ''}`}
                            className={`w-full min-h-[30px] rounded text-white text-[10px] font-medium px-1 py-1 transition-colors ${ETAPA_COLORS[col.etapa].cell}`}
                            data-testid={`crm-board-cell-${row.clientId}-${col.key}`}
                          >
                            {mark || ''}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[11px] text-gray-500">
            Cei mai recent modificați apar primii. Click pe o căsuță colorată deschide fișa clientului.
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={data.page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="crm-board-prev">
              <ChevronLeft className="h-4 w-4" /> Înapoi
            </Button>
            <span className="text-xs text-gray-600" data-testid="crm-board-page">
              Pagina {data.page} din {data.pages}
            </span>
            <Button size="sm" variant="outline" disabled={data.page >= data.pages || loading}
              onClick={() => setPage((p) => p + 1)} data-testid="crm-board-next">
              Înainte <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
